import {
  workspacePreferencesApi,
  type WorkspacePreferenceScope,
  type WorkspacePreferencesApi,
} from '../api/WorkspacePreferencesApi'
import {
  parseWorkspacePreferenceList,
  type WorkspacePreferenceEntry,
} from '../models/workspacePreference.wire'

export type WorkspacePreferenceRequestState = 'idle' | 'loading' | 'ready' | 'error'

export interface WorkspacePreferenceSnapshot<T = unknown> {
  value: T | undefined
  state: WorkspacePreferenceRequestState
  error: unknown | null
}

export interface WorkspacePreferenceListSnapshot {
  entries: readonly WorkspacePreferenceEntry[]
  state: WorkspacePreferenceRequestState
  error: unknown | null
}

interface RequestSnapshot {
  state: WorkspacePreferenceRequestState
  error: unknown | null
}

interface Partition {
  entries: Map<string, WorkspacePreferenceEntry>
  requests: Map<string, RequestSnapshot>
  list: RequestSnapshot
  listLoaded: boolean
}

function scopeKey(scope: WorkspacePreferenceScope): string {
  return `${scope.accountId}:${String(scope.workspaceId)}`
}

function assertKey(key: string): void {
  if (!key.trim()) throw new TypeError('Workspace preference key is required')
}

/** Shared account/workspace/key cache with request dedupe and active-scope generation fencing. */
export class WorkspacePreferencesService {
  private readonly partitions = new Map<string, Partition>()
  private readonly reads = new Map<string, Promise<unknown>>()
  private readonly writeTails = new Map<string, Promise<void>>()
  private readonly listeners = new Set<() => void>()
  private activeScope = ''
  private generation = 0

  constructor(private readonly api: WorkspacePreferencesApi = workspacePreferencesApi) {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  activate(scope: WorkspacePreferenceScope | null): void {
    const next = scope ? scopeKey(scope) : ''
    if (next === this.activeScope) return
    this.activeScope = next
    this.generation += 1
    this.notify()
  }

  clear(): void {
    this.partitions.clear()
    this.reads.clear()
    this.writeTails.clear()
    this.activeScope = ''
    this.generation += 1
    this.notify()
  }

  hydrate(scope: WorkspacePreferenceScope, value: unknown): boolean {
    let entries: WorkspacePreferenceEntry[]
    try {
      entries = parseWorkspacePreferenceList(value)
    } catch {
      return false
    }
    this.activate(scope)
    const partition = this.partition(scope)
    partition.entries = new Map(entries.map((entry) => [entry.key, entry]))
    partition.list = { state: 'ready', error: null }
    partition.listLoaded = true
    this.notify()
    return true
  }

  snapshot<T>(scope: WorkspacePreferenceScope | null, key: string): WorkspacePreferenceSnapshot<T> {
    if (!scope) return { value: undefined, state: 'idle', error: null }
    const partition = this.partition(scope)
    const request = partition.requests.get(key) ?? partition.list
    return {
      value: partition.entries.get(key)?.value as T | undefined,
      state: request.state,
      error: request.error,
    }
  }

  listSnapshot(scope: WorkspacePreferenceScope | null): WorkspacePreferenceListSnapshot {
    if (!scope) return { entries: [], state: 'idle', error: null }
    const partition = this.partition(scope)
    return {
      entries: [...partition.entries.values()],
      state: partition.list.state,
      error: partition.list.error,
    }
  }

  async list(
    scope: WorkspacePreferenceScope,
    options: { force?: boolean } = {},
  ): Promise<readonly WorkspacePreferenceEntry[]> {
    this.activate(scope)
    const partition = this.partition(scope)
    if (partition.listLoaded && !options.force) return [...partition.entries.values()]
    const requestKey = `list:${scopeKey(scope)}`
    const pending = this.reads.get(requestKey)
    if (pending) return pending as Promise<readonly WorkspacePreferenceEntry[]>

    const generation = this.generation
    partition.list = { state: 'loading', error: null }
    this.notify()
    const request = this.api
      .list(scope)
      .then((entries) => {
        if (!this.isCurrent(scope, generation)) return []
        partition.entries = new Map(entries.map((entry) => [entry.key, entry]))
        partition.list = { state: 'ready', error: null }
        partition.listLoaded = true
        this.notify()
        return [...partition.entries.values()]
      })
      .catch((error: unknown) => {
        if (this.isCurrent(scope, generation)) {
          partition.list = { state: 'error', error }
          this.notify()
        }
        throw error
      })
      .finally(() => {
        if (this.reads.get(requestKey) === request) this.reads.delete(requestKey)
      })
    this.reads.set(requestKey, request)
    return request
  }

  async get<T>(
    scope: WorkspacePreferenceScope,
    key: string,
    options: { force?: boolean } = {},
  ): Promise<T | undefined> {
    assertKey(key)
    this.activate(scope)
    const partition = this.partition(scope)
    const cached = partition.entries.get(key)
    if (cached && !options.force) return cached.value as T
    const requestKey = `get:${scopeKey(scope)}:${key}`
    const pending = this.reads.get(requestKey)
    if (pending) return pending as Promise<T | undefined>

    const generation = this.generation
    partition.requests.set(key, { state: 'loading', error: null })
    this.notify()
    const request = this.api
      .get<T>(scope, key)
      .then((entry) => {
        if (!this.isCurrent(scope, generation)) return
        if (entry) partition.entries.set(key, entry)
        else partition.entries.delete(key)
        partition.requests.set(key, { state: 'ready', error: null })
        this.notify()
        return entry?.value
      })
      .catch((error: unknown) => {
        if (this.isCurrent(scope, generation)) {
          partition.requests.set(key, { state: 'error', error })
          this.notify()
        }
        throw error
      })
      .finally(() => {
        if (this.reads.get(requestKey) === request) this.reads.delete(requestKey)
      })
    this.reads.set(requestKey, request)
    return request
  }

  upsert<T>(scope: WorkspacePreferenceScope, key: string, value: T): Promise<void> {
    assertKey(key)
    this.activate(scope)
    const partition = this.partition(scope)
    const requestKey = `${scopeKey(scope)}:${key}`
    const generation = this.generation
    const previous = this.writeTails.get(requestKey) ?? Promise.resolve()
    const operation = previous
      .catch(() => {})
      .then(async () => {
        if (this.isCurrent(scope, generation)) {
          partition.requests.set(key, { state: 'loading', error: null })
          this.notify()
        }
        try {
          await this.api.upsert(scope, key, value)
        } catch (error) {
          if (this.isCurrent(scope, generation)) {
            partition.requests.set(key, { state: 'error', error })
            this.notify()
          }
          throw error
        }
        if (!this.isCurrent(scope, generation)) return
        partition.entries.set(key, { key, value })
        partition.requests.set(key, { state: 'ready', error: null })
        this.notify()
      })
    this.writeTails.set(
      requestKey,
      operation.catch(() => {}),
    )
    return operation
  }

  private partition(scope: WorkspacePreferenceScope): Partition {
    const key = scopeKey(scope)
    let partition = this.partitions.get(key)
    if (!partition) {
      partition = {
        entries: new Map(),
        requests: new Map(),
        list: { state: 'idle', error: null },
        listLoaded: false,
      }
      this.partitions.set(key, partition)
    }
    return partition
  }

  private isCurrent(scope: WorkspacePreferenceScope, generation: number): boolean {
    return this.activeScope === scopeKey(scope) && this.generation === generation
  }

  private notify(): void {
    for (const listener of this.listeners) listener()
  }
}

export const workspacePreferencesService = new WorkspacePreferencesService()
